<template>
  <div class="webinar">
    <div v-if="loading" class="loading">
      <spinner size="3x"></spinner>
    </div>

    <router-view v-if="!loading && (webinarInfo || error)"></router-view>

    <modal-exit modalId="modalExit"></modal-exit>
    <modal-about modalId="modalAbout"></modal-about>
  </div>
</template>

<script>
import g2wWebinar from '@/store/modules/g2w-webinar';
import ModalExit from '@/components/modals/modal-exit';
import ModalAbout from '@/components/modals/modal-about';
import Icon from '@/components/togo/icon';
import Spinner from '@/components/togo/spinner';
import tinycolor from 'tinycolor2';
import { mapState } from 'vuex';
import errors from '@/errors';
import errorReporter from '@/utils/error-reporter';
import windowUtils from '@/utils/window-utils';

const Webinar = {
  name: 'Webinar',

  props: ['webinarKey'],

  components: {
    ModalExit,
    ModalAbout,
    Icon,
    Spinner
  },

  data() {
    return {
      loading: false
    };
  },

  computed: {
    brandingStyle() {
      return this.$store.getters['g2wWebinar/brandingStyle'];
    },

    topLineStyle() {
      const brandingColor = this.brandingStyle.headerColor;
      const lighten30 = tinycolor(brandingColor)
        .lighten(30)
        .toString();
      const lighten20 = tinycolor(brandingColor)
        .lighten(20)
        .toString();

      const colorStops = [
        lighten30,
        lighten20,
        brandingColor,
        lighten20,
        lighten30
      ];

      return {
        background: `linear-gradient(to right, ${colorStops.join(', ')})`
      };
    },

    handoutsFiles() {
      return this.$store.state.g2wSession
        ? this.$store.state.g2wSession.handouts.files
        : [];
    },

    showTopBar() {
      return !this.embed && this.$store.getters['g2wSession/inSession'];
    },

    ...mapState({
      webinarInfo: (state) => state.g2wWebinar.webinarInfo,
      sessionStatus: (state) => (state.g2wSession && state.g2wSession.sessionStatus),
      error: (state) => state.error
    })
  },

  watch: {
    // call again the method if the route changes
    $route: 'getWebinar',

    webinarInfo(newValue) {
      if (newValue && newValue.subject) {
        document.title = `GoTo Webinar - ${newValue.subject}`;
      } else {
        document.title = 'GoTo Webinar';
      }
    }
  },

  beforeCreate() {
    if (!this.$store.state.g2wWebinar) {
      this.$store.registerModule('g2wWebinar', g2wWebinar);
    }
  },

  created() {
    const context = {webinarKey: this.webinarKey};
    this.logger.log('created', {context});
    errorReporter.setContext(context);
    this.logger.setRootContext('webinarKey', this.webinarKey);

    this.getWebinar();
  },

  methods: {
    async getWebinar() {
      this.loading = true;

      try {
        const payload = {
          webinarKey: this.webinarKey,
          includes: ['branding', 'organizerInfo', 'verifyHumanRegistrant']
        };
        const webinarInfo = await this.$store.dispatch('g2wWebinar/getWebinarInfoV2', payload);

        const setLng = windowUtils.readQueryParameter('setLng', this.$route);
        if (!setLng) {
          this.$store.commit('g2wEnv/setLocale', {locale: webinarInfo.locale});
        } else {
          this.logger.log('locale overriden', {webinarLocale: webinarInfo.locale, setLng});
        }
      } catch (error) {
        this.logger.error('joinResult', { result: 'failed', error });
        this.$store.commit('setError', { error: errors.join });
      } finally {
        this.loading = false;
      }
    }
  }
};

export default Webinar;
</script>

<style lang="sass">
  @import 'src/scss/variables'
  @import 'src/scss/mixins'

  .webinar
    position: relative
    display: flex
    flex-direction: column
    height: 100%
    min-height: 100%

    .top-bar
      width: 100%
      min-height: 40px
      display: flex
      flex-direction: row-reverse
      align-items: center
      justify-content: space-between

      .btn
        cursor: pointer
        text-decoration: none
        text-transform: uppercase
        font-weight: 200
        color: var(--primary)
        padding-left: 0
        padding-right: 0

        span, .icon
          vertical-align: middle

    @include media-breakpoint-up(md)
      .card-container
        left: 20%
        right: 20%

      .card-webinar-info
        width: 100%
        height: 100%

    .fade-enter-active, .fade-leave-active
      transition: opacity .5s

    .fade-enter, .fade-leave-to
      opacity: 0

    .loading
      flex: 1
      display: flex
      justify-content: center
      align-items: center

</style>
